/**
 * Timeline / task panel date commit hints. Boolean preserves legacy `rippleDependents` API.
 */
export type CommitTaskDatesOptions = {
  /** Gantt: Shift held — skip dependency prompt and use forward ripple when applicable */
  preferRipple?: boolean;
  /** Set when user already chose in the dependency modal, or for internal apply */
  commitMode?: 'direct' | 'ripple' | 'shiftConnected';
  /**
   * Task detail / table date fields: always show the dependency modal when the task has
   * edges (the “auto-schedule dependents” checkbox only applies when there are no links).
   * Do not silently apply a gantt “don’t ask again” preference on this path (CRM #40).
   */
  fromTaskPanel?: boolean;
};

export type CommitTaskDatesHint = boolean | CommitTaskDatesOptions;

export type DependencyCommitMode = 'direct' | 'ripple' | 'shiftConnected';

export type TaskDateCommitDecision =
  | { action: 'apply'; mode: DependencyCommitMode }
  | { action: 'prompt' };

export function normalizeCommitTaskDatesHint(hint: CommitTaskDatesHint): CommitTaskDatesOptions {
  if (typeof hint === 'boolean') return { preferRipple: hint };
  return {
    preferRipple: hint.preferRipple ?? false,
    commitMode: hint.commitMode,
    fromTaskPanel: hint.fromTaskPanel,
  };
}

/**
 * Decide whether to apply a date commit immediately or open the dependency modal.
 *
 * Gantt drag may reuse a per-project “don’t ask again” preference. Task panel / table
 * date fields must still prompt when the task has dependency edges so cascade/shift
 * cannot silently clamp dates back (CRM #40).
 */
export function decideTaskDateCommit(opts: {
  datesChanged: boolean;
  hasDeps: boolean;
  hasProjectId: boolean;
  preferRipple: boolean;
  fromTaskPanel: boolean;
  remembered: DependencyCommitMode | null;
}): TaskDateCommitDecision {
  const { datesChanged, hasDeps, hasProjectId, preferRipple, fromTaskPanel, remembered } = opts;

  if (!datesChanged) {
    return { action: 'apply', mode: preferRipple ? 'ripple' : 'direct' };
  }

  if (hasDeps && hasProjectId) {
    // Panel/table: always ask — ignore gantt remembered preference.
    if (fromTaskPanel) {
      return { action: 'prompt' };
    }
    if (remembered) {
      return { action: 'apply', mode: remembered };
    }
    if (!preferRipple) {
      return { action: 'prompt' };
    }
  }

  return { action: 'apply', mode: preferRipple ? 'ripple' : 'direct' };
}
